import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSolvents, type CommonSolvent } from '@peruselab/api-client/assertions';
import { searchMoleculesByText, type MoleculeResult } from '@peruselab/api-client/moleculeSearch';

const OTHER = '__other__';
const SEARCH_DELAY_MS = 300;
const SHOWN_RESULTS = 6;

let solventsOnce: Promise<readonly CommonSolvent[]> | null = null;
const loadSolvents = () => {
  solventsOnce ??= getSolvents().catch((failure: unknown) => {
    solventsOnce = null;
    throw failure;
  });
  return solventsOnce;
};

interface SolventPickerProps {
  /** A common solvent's key, or a Molecule's id. */
  readonly value: string;
  readonly label: string | null;
  readonly onChange: (value: string, label: string) => void;
}

/**
 * ADR 0018 D5 (#920): a solvent is a Molecule. The common ones are one tap away (picking one links
 * its Molecule, registered once on the server); any other is found by name or SMILES through the
 * molecule search.
 */
export function SolventPicker({ value, label, onChange }: SolventPickerProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const [common, setCommon] = useState<readonly CommonSolvent[]>([]);
  const [other, setOther] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly MoleculeResult[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadSolvents()
      .then((solvents) => {
        if (!cancelled) setCommon(solvents);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const text = query.trim();
    if (!other || text.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      searchMoleculesByText(text)
        .then((found) => {
          if (!cancelled) setResults(found.molecules.slice(0, SHOWN_RESULTS));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [other, query]);

  const isCommon = common.some((solvent) => solvent.key === value);
  return (
    <div className="solvent-picker">
      <select
        aria-label={t('properties.form.solvent')}
        value={other || (value && !isCommon) ? OTHER : value}
        onChange={(event) => {
          if (event.target.value === OTHER) {
            setOther(true);
            return;
          }
          setOther(false);
          const chosen = common.find((solvent) => solvent.key === event.target.value);
          if (chosen) onChange(chosen.key, chosen.name);
        }}
      >
        <option value="" disabled>
          {t('properties.form.chooseSolvent')}
        </option>
        {common.map((solvent) => (
          <option key={solvent.key} value={solvent.key}>
            {solvent.name}
          </option>
        ))}
        <option value={OTHER}>{value && !isCommon && label ? label : t('properties.form.otherSolvent')}</option>
      </select>
      {other ? (
        <div className="solvent-picker-search">
          <label htmlFor={searchId} className="visually-hidden">
            {t('properties.form.findSolvent')}
          </label>
          <input
            id={searchId}
            type="search"
            dir="auto"
            placeholder={t('properties.form.findSolvent')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {searching ? <p className="discussion-note">{t('common.loading')}</p> : null}
          {results.length > 0 ? (
            <ul className="solvent-picker-results" aria-label={t('properties.form.solventResults')}>
              {results.map((result) => (
                <li key={result.moleculeId}>
                  <button
                    type="button"
                    className="discussion-link-button"
                    onClick={() => {
                      onChange(result.moleculeId, result.name ?? result.moleculeId.slice(0, 8));
                      setOther(false);
                    }}
                  >
                    {result.name ?? result.moleculeId.slice(0, 8)}
                  </button>
                </li>
              ))}
            </ul>
          ) : query.trim().length >= 2 && !searching ? (
            <p className="discussion-note">{t('properties.form.noSolventFound')}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
